import { Button, Card, ModalBody, ModalContent, ModalFooter, ScrollShadow } from '@nextui-org/react'
import { FC } from 'react'
import useHomeRemoveGroupStore, { TankToRemove } from '../../store/home/useHomeRemoveGroupStore'
import { useBatchStore } from '../../store/batch/useBatchStore'
import { useGroupStore } from '../../store/group/useGroupStore'
import { BaseDivider, BaseModal, BaseModalHeader } from '../common/Modal'

export interface SelectGroupForRemoveProps {
  isOpen: boolean
  onOpen: () => void
  onOpenChange: () => void
}

export const SelectGroupForRemove: FC<SelectGroupForRemoveProps> = ({
  isOpen,
  onOpenChange,
  onOpen,
}) => {
  const {
    selectedGroupId,
    setSelectedGroup,
    setGroupBorderColor,
    selectGroupBorderColor,
    setTanksInGroup,
  } = useHomeRemoveGroupStore()

  const batches = useBatchStore((state) => state.batches)
  const groupsWithDetails = useGroupStore((state) => state.groupsWithDetails)

  // Filter to get only real groups (not $empty or $ungrouped)
  const groupList = Object.entries(batches)
    .filter(([batchId]) => batchId !== '$empty' && batchId !== '$ungrouped')
    .map(([batchId, batchInfo]) => ({
      id: batchId,
      name: batchInfo.batchName,
    }))

  const handleGroupSelect = (groupId: string, groupName: string) => {
    setSelectedGroup(groupId, groupName)

    // Load tanks for the selected group
    const group = groupsWithDetails.find((g) => g.id === groupId)
    if (group && group.items) {
      const tanks: TankToRemove[] = group.items.map((item) => ({
        tankId: item.tank.id,
        tankName: item.tank.name,
        schoolId: item.school.id,
      }))
      setTanksInGroup(tanks)
    }
  }

  const checkGroupBeforeContinue = (onClose1: () => void, onOpenNext: () => void) => {
    setGroupBorderColor()
    if (selectedGroupId !== null) {
      onOpenNext()
      setTimeout(() => {
        onClose1()
      }, 50)
    }
  }

  return (
    <BaseModal isOpen={isOpen} onOpenChange={onOpenChange} size={'3xl'} isDismissable={true}>
      <ModalContent className="overflow-x-hidden">
        {(onClose) => (
          <>
            <BaseModalHeader
              title="Remove from Group"
              description="Select a group to remove tanks from"
            />
            <ModalBody className="gap-1 min-h-[8rem] pl-6 sm:pl-10 pr-6 sm:pr-10 overflow-x-hidden overflow-y-auto">
              <BaseDivider />

              <div className="mt-3 font-bold">Select Group</div>
              <Card
                className={`${selectGroupBorderColor} border-opacity-60 p-0`}
                radius="md"
                shadow="sm"
              >
                <ScrollShadow className="max-h-[350px]">
                  <div className="flex flex-col gap-2 p-3">
                    {groupList.length === 0 ? (
                      <div className="text-center text-gray-500 py-4">
                        No groups available.
                      </div>
                    ) : (
                      groupList.map((group) => (
                        <Card
                          key={group.id}
                          isPressable
                          onPress={() => handleGroupSelect(group.id, group.name)}
                          className={`p-4 transition-all duration-200 ${
                            selectedGroupId === group.id
                              ? 'bg-tdplsky border-2 border-tdplprussian shadow-md'
                              : 'bg-white border border-tdpldarkgrey border-opacity-40 hover:bg-tdpllightgrey hover:border-opacity-70'
                          }`}
                          radius="md"
                          shadow="none"
                        >
                          <span
                            className={`font-semibold ${
                              selectedGroupId === group.id
                                ? 'text-tdplprussian'
                                : 'text-tdpldarkgrey'
                            }`}
                          >
                            {group.name}
                          </span>
                        </Card>
                      ))
                    )}
                  </div>
                </ScrollShadow>
              </Card>

              {selectGroupBorderColor.includes('tdplmaroon') && (
                <p className="m-0 text-sm font-semibold text-tdplmaroon">
                  Please select a group.
                </p>
              )}
            </ModalBody>

            <ModalFooter className="pb-6 sm:pb-8 pl-6 sm:pl-10 pr-6 sm:pr-10">
              <div className="flex gap-3 justify-end w-full">
                <Button
                  variant="light"
                  className="font-bold text-tdplindigo border border-tdplindigo"
                  onPress={onClose}
                >
                  Cancel
                </Button>
                <Button
                  className="font-bold text-white w-[6rem] mr-0 bg-tdplplum"
                  onPress={() => {
                    checkGroupBeforeContinue(onClose, onOpen)
                  }}
                  isDisabled={groupList.length === 0}
                >
                  Continue
                </Button>
              </div>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </BaseModal>
  )
}
